import type { ContactChannel } from '@/types/content'
import { useContent } from '@/content'
import type { IconName } from '@/components/ui'
import { Icon, Surface } from '@/components/ui'
import styles from './ContactDetails.module.css'

/** Maps a channel to its glyph. */
const iconFor = (channel: ContactChannel): IconName => {
  if (channel.href?.startsWith('tel:')) return 'phone'
  if (channel.href?.startsWith('mailto:')) return 'envelope'
  return 'mapPin'
}

/** Address, phone and email above a desaturated map. */
/**
 * An OpenStreetMap embed centred on a point, as a roughly 11 km box.
 *
 * Derived from the site data rather than pasted in, because the pasted
 * version was the template's Amsterdam coordinates sitting under a heading
 * that read "Yaoundé, Cameroon".
 */
const mapEmbedUrl = ({ lat, lon }: { lat: number; lon: number }): string => {
  const pad = 0.05
  const bbox = [lon - pad, lat - pad, lon + pad, lat + pad]
    .map((n) => n.toFixed(4))
    .join(',')
  return `https://www.openstreetmap.org/export/embed.html?bbox=${encodeURIComponent(bbox)}&layer=mapnik&marker=${lat},${lon}`
}

export const ContactDetails = () => {
  const { contactChannels: CONTACT_CHANNELS, site } = useContent()

  return (
  <Surface pad={20} radius="card" className={styles.panel}>
    <div className={styles.head}>
      <h2 className="t-24">Get in touch</h2>
      <ul className={styles.info}>
        {CONTACT_CHANNELS.map((channel) => (
          <li key={channel.label} className={`t-14 ${styles.row}`}>
            <Icon name={iconFor(channel)} size={15} className={styles.icon} />
            {channel.href ? (
              <a href={channel.href} className={styles.value}>
                {channel.value}
              </a>
            ) : (
              <span className={styles.value}>{channel.value}</span>
            )}
          </li>
        ))}
      </ul>
    </div>

    <div className={styles.mapFrame}>
      <iframe
        className={styles.map}
        title={`Map showing ${site.name} studio location`}
        loading="lazy"
        referrerPolicy="no-referrer-when-downgrade"
        src={mapEmbedUrl(site.coordinates)}
      />
    </div>
  </Surface>
  )
}
